import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    currency: "USD",
    currencyFrom: "USD",
    amount: "",
    narration: "",
    pin: "",
    sesid: "",
    rateKey: "",
    beneficiaryId: "",
    transactionId: "",
    bankName: "",
    accountOwnerName: "",
    accountOwnerType: "individual", // 'individual' | 'corporate'
    accountType: "us", // 'us' | 'iban' | 'international_wire'
    paymentRail: "ach", // 'ach' | 'rtp' | 'wire' | 'swift'

    account: {
        accountNumber: "",
        routingNumber: "",
        checkingOrSavings: "checking", // 'checking' | 'savings'
    },

    // IBAN method
    iban: {
        accountNumber: "",
        bic: "",
        country: "",
    },

    // SWIFT method
    swift: {
        accountNumber: "",
        bic: "",
        country: "",
    },

    // identity (person or business)
    firstName: "",
    lastName: "",
    businessName: "",

    // address
    address: {
        streetLine1: "",
        streetLine2: "",
        city: "",
        state: "",
        subdivision: "",
        postalCode: "",
        country: "US",
    },
    bankAddress: {
        streetLine1: "",
        streetLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "US",
    },
    relationship: "",
    email: "",

    // attachments
    invoice: null,
    supportDocument: null,
};

const usdTransferSlice = createSlice({
    name: "usdTransfer",
    initialState,
    reducers: {
        // primitives
        setUsdAmount: (s, a) => {
            s.amount = a.payload;
        },
        setUsdNarration: (s, a) => {
            s.narration = a.payload;
        },
        setUsdPin: (s, a) => {
            s.pin = a.payload;
        },
        setUsdSesid: (s, a) => {
            s.sesid = a.payload;
        },
        setUsdRateKey: (s, a) => {
            s.rateKey = a.payload || "";
        },

        // method + meta
        setUsdAccountType: (s, a) => {
            s.accountType = a.payload;
        }, // 'us' | 'iban' | 'international_wire'
        setUsdPaymentRail: (s, a) => {
            s.paymentRail = a.payload;
        }, // 'ach' | 'rtp' | 'wire' | 'swift'
        setUsdBeneficiaryId: (s, a) => {
            s.beneficiaryId = a.payload || "";
        },
        setUsdRecipientMeta: (s, a) => {
            const { bankName, accountOwnerName, accountOwnerType, accountType } = a.payload || {};
            if (bankName !== undefined) s.bankName = bankName;
            if (accountOwnerName !== undefined) s.accountOwnerName = accountOwnerName;
            if (accountOwnerType !== undefined) s.accountOwnerType = accountOwnerType;
            if (accountType !== undefined) s.accountType = accountType;
        },

        // identity
        setUsdIdentity: (s, a) => {
            const { firstName, lastName, businessName } = a.payload || {};
            if (firstName !== undefined) s.firstName = firstName;
            if (lastName !== undefined) s.lastName = lastName;
            if (businessName !== undefined) s.businessName = businessName;
        },

        // account blocks
        setUsdAccount: (s, a) => {
            s.account = { ...s.account, ...(a.payload || {}) };
        },
        setUsdIban: (s, a) => {
            s.iban = { ...s.iban, ...(a.payload || {}) };
        },
        setUsdSwift: (s, a) => {
            s.swift = { ...s.swift, ...(a.payload || {}) };
        },

        // address
        setUsdAddress: (s, a) => {
            s.address = { ...s.address, ...(a.payload || {}) };
        },

        // docs
        setUsdInvoice: (s, a) => {
            s.invoice = a.payload;
        },
        setUsdSupportDocument: (s, a) => {
            s.supportDocument = a.payload;
        },

        setUsdAll: (s, a) => {
            const p = a.payload || {};

            // top-level
            if (p.currency !== undefined) s.currency = p.currency;
            if (p.currencyFrom !== undefined) s.currencyFrom = p.currencyFrom;
            if (p.amount !== undefined) s.amount = p.amount;
            if (p.narration !== undefined) s.narration = p.narration;
            if (p.naration !== undefined) s.narration = p.naration;
            if (p.pin !== undefined) s.pin = p.pin;
            if (p.sesid !== undefined) s.sesid = p.sesid;
            if (p.rateKey !== undefined) s.rateKey = p.rateKey;
            if (p.beneficiaryId !== undefined) s.beneficiaryId = p.beneficiaryId;
            if (p.transactionId !== undefined) s.transactionId = p.transactionId;
            if (p.sessionId !== undefined && !s.sesid) s.sesid = String(p.sessionId);
            if (p.sessionID !== undefined && !s.sesid) s.sesid = String(p.sessionID);
            if (p.bankName !== undefined) s.bankName = p.bankName;
            if (p.accountOwnerName !== undefined) s.accountOwnerName = p.accountOwnerName;
            if (p.accountOwnerType !== undefined) s.accountOwnerType = p.accountOwnerType;
            if (p.accountType !== undefined) s.accountType = p.accountType;
            if (p.paymentRail !== undefined) s.paymentRail = p.paymentRail;
            if (p.relationship !== undefined) s.relationship = p.relationship;
            if (p.email !== undefined) s.email = p.email;

            if (p.firstName !== undefined) s.firstName = p.firstName;
            if (p.lastName !== undefined) s.lastName = p.lastName;
            if (p.businessName !== undefined) s.businessName = p.businessName;

            if (p.invoice !== undefined) s.invoice = p.invoice;
            if (p.supportDocument !== undefined) s.supportDocument = p.supportDocument;

            // nested merges (camelCase)
            if (p.account) s.account = { ...s.account, ...p.account };
            if (p.iban) s.iban = { ...s.iban, ...p.iban };
            if (p.swift) s.swift = { ...s.swift, ...p.swift };
            if (p.address) s.address = { ...s.address, ...p.address };
            if (p.bankAddress) s.bankAddress = { ...s.bankAddress, ...p.bankAddress };

            // snake_case inputs (API-shaped)
            if (p.account_type !== undefined) s.accountType = p.account_type;
            if (p.account_owner_name !== undefined) s.accountOwnerName = p.account_owner_name;
            if (p.account_owner_type !== undefined) s.accountOwnerType = p.account_owner_type;
            if (p.bank_name !== undefined) s.bankName = p.bank_name;
            if (p.rateKey !== undefined) s.rateKey = p.rateKey;
            if (p.payment_rail !== undefined) s.paymentRail = p.payment_rail;
            if (p.bank_address) {
                const bankAddress = p.bank_address;
                s.bankAddress = {
                    ...s.bankAddress,
                    streetLine1:
                        bankAddress.street_line_1 ??
                        bankAddress.streetLine1 ??
                        s.bankAddress.streetLine1,
                    streetLine2:
                        bankAddress.street_line_2 ??
                        bankAddress.streetLine2 ??
                        s.bankAddress.streetLine2,
                    city: bankAddress.city ?? s.bankAddress.city,
                    state: bankAddress.state ?? s.bankAddress.state,
                    postalCode:
                        bankAddress.postal_code ??
                        bankAddress.postalCode ??
                        s.bankAddress.postalCode,
                    country: bankAddress.country ?? s.bankAddress.country,
                };
            }

            if (p.account) {
                const acc = p.account;
                s.account = {
                    ...s.account,
                    accountNumber:
                        acc.account_number ?? acc.accountNumber ?? s.account.accountNumber,
                    routingNumber:
                        acc.routing_number ?? acc.routingNumber ?? s.account.routingNumber,
                    checkingOrSavings:
                        acc.checking_or_savings ??
                        acc.checkingOrSavings ??
                        s.account.checkingOrSavings,
                };
            }
            if (p.iban) {
                const ib = p.iban;
                s.iban = {
                    ...s.iban,
                    accountNumber: ib.account_number ?? ib.accountNumber ?? s.iban.accountNumber,
                    bic: ib.bic ?? s.iban.bic,
                    country: ib.country ?? s.iban.country,
                };
            }
            if (p.swift) {
                const sw = p.swift;
                s.swift = {
                    ...s.swift,
                    accountNumber: sw.account_number ?? sw.accountNumber ?? s.swift.accountNumber,
                    bic: sw.bic ?? s.swift.bic,
                    country: sw.country ?? s.swift.country,
                };
            }
            if (p.address) {
                const ad = p.address;
                s.address = {
                    ...s.address,
                    streetLine1: ad.street_line_1 ?? ad.streetLine1 ?? s.address.streetLine1,
                    streetLine2: ad.street_line_2 ?? ad.streetLine2 ?? s.address.streetLine2,
                    city: ad.city ?? s.address.city,
                    state: ad.state ?? s.address.state,
                    subdivision: ad.subdivision ?? s.address.subdivision,
                    postalCode: ad.postal_code ?? ad.postalCode ?? s.address.postalCode,
                    country: ad.country ?? s.address.country,
                };
            }

            // Legacy “recipient” block compatibility
            if (p.recipient) {
                const r = p.recipient;
                if (r.bank_name !== undefined) s.bankName = r.bank_name;
                if (r.account_owner_name !== undefined) s.accountOwnerName = r.account_owner_name;
                if (r.account_owner_type !== undefined) s.accountOwnerType = r.account_owner_type;
                if (r.account_type !== undefined) s.accountType = r.account_type;
                if (r.payment_rail !== undefined) s.paymentRail = r.payment_rail;
                if (r.relationship !== undefined) s.relationship = r.relationship;
                if (r.email !== undefined) s.email = r.email;

                const inferredId =
                    r.bridgeBeneficiaryId || r.beneficiaryId || r.beneficiary_id || "";
                if (inferredId) s.beneficiaryId = inferredId;

                if (r.account) {
                    s.account = {
                        ...s.account,
                        accountNumber: r.account.account_number ?? s.account.accountNumber,
                        routingNumber: r.account.routing_number ?? s.account.routingNumber,
                        checkingOrSavings:
                            r.account.checking_or_savings ?? s.account.checkingOrSavings,
                    };
                }
                if (r.iban) {
                    s.iban = {
                        ...s.iban,
                        accountNumber: r.iban.account_number ?? s.iban.accountNumber,
                        bic: r.iban.bic ?? s.iban.bic,
                        country: r.iban.country ?? s.iban.country,
                    };
                }
                if (r.swift) {
                    s.swift = {
                        ...s.swift,
                        accountNumber: r.swift.account_number ?? s.swift.accountNumber,
                        bic: r.swift.bic ?? s.swift.bic,
                        country: r.swift.country ?? s.swift.country,
                    };
                }
                if (r.address) {
                    s.address = {
                        ...s.address,
                        streetLine1: r.address.street_line_1 ?? s.address.streetLine1,
                        streetLine2: r.address.street_line_2 ?? s.address.streetLine2,
                        city: r.address.city ?? s.address.city,
                        state: r.address.state ?? s.address.state,
                        subdivision: r.address.subdivision ?? s.address.subdivision,
                        postalCode: r.address.postal_code ?? s.address.postalCode,
                        country: r.address.country ?? s.address.country,
                    };
                }
                if (r.bank_address) {
                    s.bankAddress = {
                        ...s.bankAddress,
                        streetLine1:
                            r.bank_address.street_line_1 ?? s.bankAddress.streetLine1,
                        streetLine2:
                            r.bank_address.street_line_2 ?? s.bankAddress.streetLine2,
                        city: r.bank_address.city ?? s.bankAddress.city,
                        state: r.bank_address.state ?? s.bankAddress.state,
                        postalCode: r.bank_address.postal_code ?? s.bankAddress.postalCode,
                        country: r.bank_address.country ?? s.bankAddress.country,
                    };
                }
            }
        },

        resetUsdTransfer: () => initialState,
    },
});

export const {
    setUsdAmount,
    setUsdNarration,
    setUsdPin,
    setUsdSesid,
    setUsdRateKey,
    setUsdBeneficiaryId,
    setUsdRecipientMeta,
    setUsdAccountType,
    setUsdPaymentRail,
    setUsdAccount,
    setUsdIban,
    setUsdSwift,
    setUsdIdentity,
    setUsdAddress,
    setUsdInvoice,
    setUsdSupportDocument,
    setUsdAll,
    resetUsdTransfer,
} = usdTransferSlice.actions;

export const selectUsdMethod = (state) => state.usdTransfer?.accountType || "us";

export const selectUsdTransferPayload = (state) => {
    const s = state.usdTransfer || initialState;
    const paymentRail =
        (s.paymentRail === "iban" ? "wire" : s.paymentRail) ||
        (s.accountType === "iban"
            ? "wire"
            : s.accountType === "international_wire" || s.accountType === "swift"
              ? "swift"
              : "ach");

    const base = {
        amount: String(s.amount ?? ""),
        sesid: s.sesid || "",
        pin: s.pin || "",
        currency: s.currency || "USD",
        currencyFrom: s.currencyFrom || s.currency || "USD",
        account_owner_name: s.accountOwnerName || "",
        account_type: s.accountType || "us",
        paymentRail,
        account_owner_type: s.accountOwnerType || "individual",
        first_name: s.firstName || "",
        last_name: s.lastName || "",
        business_name: s.businessName || "",
        address: {
            street_line_1: s.address.streetLine1 || "",
            street_line_2: s.address.streetLine2 || "",
            city: s.address.city || "",
            state: s.address.state || "",
            subdivision: s.address.subdivision || "",
            postal_code: s.address.postalCode || "",
            country: s.address.country || "",
        },
        relationship: s.relationship || "",
        email: s.email || "",
        naration: s.narration || "", // API spelling
        beneficiaryId: s.beneficiaryId || "",
        transactionId: s.transactionId || "",
        rateKey: s.rateKey || "",
    };

    if (s.accountType === "us") {
        base.bank_name = s.bankName || "";
        base.account = {
            account_number: s.account.accountNumber || "",
            routing_number: s.account.routingNumber || "",
            checking_or_savings: s.account.checkingOrSavings || "checking",
        };
        base.bank_address = {
            street_line_1: s.bankAddress.streetLine1 || "",
            street_line_2: s.bankAddress.streetLine2 || "",
            city: s.bankAddress.city || "",
            state: s.bankAddress.state || "",
            postal_code: s.bankAddress.postalCode || "",
            country: s.bankAddress.country || "",
        };
    } else if (s.accountType === "iban") {
        base.bank_name = s.bankName || "";
        base.iban = {
            account_number: s.iban.accountNumber || "",
            bic: s.iban.bic || "",
            country: s.iban.country || "",
        };
    } else if (s.accountType === "international_wire" || s.accountType === "swift") {
        base.bank_name = s.bankName || "";
        base.account_type = "international_wire";
        base.swift = {
            account_number: s.swift.accountNumber || "",
            bic: s.swift.bic || "",
            country: s.swift.country || "",
            ...(s.swift.routingNumber ? { routing_number: s.swift.routingNumber } : {}),
        };
    }

    return base;
};

export const selectUsdTransfer = (state) => state.usdTransfer;
export default usdTransferSlice.reducer;
